import { createSlice } from '@reduxjs/toolkit';
import { IClearingAccount } from '../../components/ClearingAccountModal';
import { IPaymentGatewayDetail, IShopifySettingsPutRequest } from '../../models';
import { RootState } from '../rootReducer';
import { AppThunk, deepClone } from '../store';
import { getSettingsAction, putSettingsAction, updateSettingsBegin, updateSettingsSuccess } from './SettingsSlice';

export interface IAccountSetupState {
    error?: Error;
    isCreating: boolean;
}

export const initialState: IAccountSetupState = {
    isCreating: false,
};

const accountSetupSlice = createSlice({
    name: 'clearingAccount',
    initialState,
    reducers: {
        createClearingAccountStart(state) {
            state.isCreating = true;
        },
        createClearingAccountSuccess(state) {
            state.isCreating = false;
            state.error = undefined;
        },
        createClearingAccountFail(state, { payload }) {
            state.isCreating = false;
            state.error = payload;
        },
    },
});

export const createClearingAccount =
    (newClearingAccount: IClearingAccount, gateway: string): AppThunk =>
    async (dispatch, getState) => {
        if (newClearingAccount.bankAccountName) {
            dispatch(createClearingAccountStart());

            const settings = deepClone(getState().settings.settings) as IShopifySettingsPutRequest;
            if (settings.paymentGatewayDetails && settings.paymentGatewayDetails[gateway]) {
                settings.paymentGatewayDetails[gateway] = {
                    clearingAccountId: null,
                    bankAccountId: settings.paymentGatewayDetails[gateway].bankAccountId,
                    clearingAccountName: newClearingAccount.bankAccountName,
                    createInvoice: true,
                };
            } else if (settings.paymentGatewayDetails) {
                settings.paymentGatewayDetails[gateway] = {
                    clearingAccountName: newClearingAccount.bankAccountName,
                    createInvoice: true,
                };
            } else {
                settings.paymentGatewayDetails = {
                    [gateway]: {
                        clearingAccountName: newClearingAccount.bankAccountName,
                        createInvoice: true,
                    },
                };
            }

            try {
                await dispatch(putSettingsAction(settings)).unwrap();
                await dispatch(getSettingsAction());

                dispatch(createClearingAccountSuccess());
            } catch (error) {
                dispatch(createClearingAccountFail(error));
            }
        }
    };

const updatePaymentGatewayDetails =
    (key: string, details: Partial<IPaymentGatewayDetail>): AppThunk =>
    (dispatch, getState) => {
        const settings = getState().settings.settings as IShopifySettingsPutRequest;
        const paymentGatewayDetails = deepClone(settings.paymentGatewayDetails);
        if (paymentGatewayDetails) {
            paymentGatewayDetails[key] = {
                ...paymentGatewayDetails[key],
                ...details,
            };
        }
        dispatch(updateSettingsBegin());
        dispatch(updateSettingsSuccess({ paymentGatewayDetails } as Partial<IShopifySettingsPutRequest>));
    };

export const paymentGatewayChanged = (paymentGatewayKey: string, paymentGateway: IPaymentGatewayDetail): AppThunk =>
    updatePaymentGatewayDetails(paymentGatewayKey, paymentGateway);

export const clearingAccountRemoved = (paymentGatewayKey: string): AppThunk =>
    updatePaymentGatewayDetails(paymentGatewayKey, {
        clearingAccountId: null,
        clearingAccountCurrency: null,
    });

export const bankAccountRemoved = (paymentGatewayKey: string): AppThunk =>
    updatePaymentGatewayDetails(paymentGatewayKey, {
        bankAccountCurrency: null,
        bankAccountId: null,
    });

// Action creators are generated for each case reducer function
export const { createClearingAccountStart, createClearingAccountSuccess, createClearingAccountFail } =
    accountSetupSlice.actions;

export const clearingAccountSelectors = {
    paymentGatewayDetails: (store: RootState): Record<string, IPaymentGatewayDetail> =>
        (store.settings.settings as IShopifySettingsPutRequest).paymentGatewayDetails ?? {},
    isCreating: (store: RootState) => store.clearingAccount.isCreating,
    error: (store: RootState) => store.clearingAccount.error,
};

export default accountSetupSlice.reducer;
